import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CalendarClock, Check, X, RotateCcw, Phone, Radio, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { formatDate } from "../lib/constants";
import { FollowUpComposer } from "./FollowUpComposer";

const quando = (iso) => new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome", weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const FollowUpCard = ({ item, onChange, compact }) => {
  const [open, setOpen] = useState(false);
  const isApp = item.tipo === "appuntamento";
  const act = async (what) => {
    try {
      if (what === "delete") await api.delete(`/followups/${item.id}`);
      else await api.post(`/followups/${item.id}/${what}`);
      onChange?.();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const Origin = item.origine_tipo === "chiamata" ? Phone : Radio;
  const originTo = item.origine_tipo === "chiamata" ? "/chiamate" : `/live/${item.origine_id}`;
  return (
    <li data-testid={`followup-${item.id}`} className={`surface p-4 ${item.stato === "aperto" ? (isApp ? "border-[#F5B942]/40" : "border-primary/30") : "opacity-70"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${isApp ? "bg-[#F5B942]/15 text-[#FCD990]" : "bg-primary/15 text-primary"}`}>
            {isApp ? <CalendarClock className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
          </div>
          <div className="min-w-0">
            <p data-testid="followup-oggetto" className="font-semibold leading-snug">{item.oggetto}</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span data-testid="followup-cliente">{item.cliente_nome || "Cliente"}{item.cliente_email ? ` · ${item.cliente_email}` : <span className="text-amber-300"> · email mancante</span>}</span>
              {isApp && item.appuntamento?.inizio && <span className="font-mono text-[#FCD990]">{quando(item.appuntamento.inizio)}</span>}
              {!compact && <Link to={originTo} data-testid="followup-origine" className="inline-flex items-center gap-1 hover:text-white"><Origin className="h-3 w-3" /> {item.origine_titolo || item.origine_tipo}</Link>}
              <span>{formatDate(item.created_at)}</span>
            </p>
            {!compact && item.motivo && <p data-testid="followup-motivo" className="mt-2 text-xs italic text-muted-foreground">“{item.motivo}”</p>}
            {item.stato === "inviato" && (
              <p data-testid="followup-sent" className="mt-2 inline-flex flex-wrap items-center gap-2 text-xs text-emerald-300">
                <Check className="h-3.5 w-3.5" /> Inviata il {formatDate(item.sent_at)} a {item.sent_to?.join(", ")}
                {item.calendar_link && <a href={item.calendar_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">Google Calendar <ExternalLink className="h-3 w-3" /></a>}
              </p>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {item.stato === "aperto" ? (
            <>
              <button data-testid={`followup-write-${item.id}`} onClick={() => setOpen(true)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white transition-all hover:-translate-y-0.5 ${isApp ? "bg-[#B8860B]" : "bg-primary"}`}>
                {isApp ? <CalendarClock className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />} {isApp ? "Conferma e invita" : "Scrivi mail"}
              </button>
              <button data-testid={`followup-ignore-${item.id}`} onClick={() => act("ignora")} title="Ignora" className="rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-white"><X className="h-4 w-4" /></button>
            </>
          ) : (
            <>
              {item.stato === "ignorato" && <button data-testid={`followup-reopen-${item.id}`} onClick={() => act("riapri")} title="Riapri" className="rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-white"><RotateCcw className="h-4 w-4" /></button>}
              <button data-testid={`followup-delete-${item.id}`} onClick={() => act("delete")} title="Elimina" className="rounded-md p-2 text-muted-foreground hover:bg-red-500/15 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
            </>
          )}
        </div>
      </div>
      <FollowUpComposer item={open ? item : null} open={open} onClose={() => setOpen(false)} onSent={onChange} />
    </li>
  );
};

/** Lista follow-up di una sorgente (sessione live o chiamata). Non mostra nulla se vuota. */
export const FollowUpList = ({ origineId, title = "Impegni presi con il cliente", compact }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/followups", { params: { origine_id: origineId, stato: "tutti" } }).then((r) => setItems(r.data.items)).catch(() => setItems([])), [origineId]);
  useEffect(() => { load(); }, [load]);
  if (!items?.length) return null;
  return (
    <section data-testid={`followup-list-${origineId}`} className="space-y-3">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Mail className="h-4 w-4 text-primary" />{title} <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-mono text-primary">{items.filter((i) => i.stato === "aperto").length} da fare</span></h2>
      <ul className="space-y-3">{items.map((it) => <FollowUpCard key={it.id} item={it} onChange={load} compact={compact} />)}</ul>
    </section>
  );
};
